import '@aws-amplify/pubsub';
import '@aws-amplify/ui-react/styles.css';
import { useEffect, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { Route, Routes } from 'react-router-dom';
import { ToastId, useToast } from '@chakra-ui/react';
import { AppProviders } from './components/AppProviders/AppProviders';
import { initGA } from './features/analytic/analytics';
import { RouteObserver } from './features/analytic/RouteObserver';
import MeetingStatsViewContextProvider from './features/meeting-stats/context/MeetingStatsViewContext';
import LiveMeetingPermissions from './features/meeting/LiveMeetingPermissions';
// Remove legacy WebcamStreamManager import
import AdminPage from './pages/admin/AdminPage';
import { VideoCallWrapper } from './pages/caseCluster/VideoCallWrapper';
import { GlobalGuard } from './pages/globalGuard/GlobalGuard';
import HomeView from './pages/home/HomeView';
import { MeetingStatsView } from './pages/meeting-stats/MeetingStatsView/MeetingStatsView';
import EmptyPageWithTitle from './pages/page404/EmptyPageWithTitle.tsx';
import PreviewCasePermissions from './pages/previewCase/PreviewCasePermissions';
import PreviewCaseCodeView from './pages/previewCaseCodeView/PreviewCaseCodeView';
import PreviewMeetingPermissions from './pages/previewMeeting/PreviewMeetingPermissions';
import AdminTestPage from './pages/test/AdminTestPage';
import { LiveKitTestPage } from './pages/test/LiveKitTestPage';
import TestPage from './pages/test/TestPage';
import AdminRoute from './routes/AdminRoute';
import { ROUTES } from './routes/Routes';
import { ErrorFallback } from './ui/ErrorFallback/ErrorFallback';
import useNetworkConnection from './utils/helpers/hooks/useNetwork';
import { reloadApp } from './utils/reloadApp';

const App = () => {
  const { online } = useNetworkConnection();
  const { t } = useTranslation();
  const toast = useToast();
  const offlineNotificationToastRef = useRef<ToastId | null>(null);

  // Initialize Google Analytics once when app loads
  useEffect(() => {
    initGA();
  }, []);

  if (!online) {
    offlineNotificationToastRef.current = toast({
      status: 'warning',
      title: t('warning.noNetwork'),
      size: 'md',
      position: 'top',
      isClosable: false,
      duration: null,
    });
  } else {
    if (offlineNotificationToastRef.current) {
      toast.close(offlineNotificationToastRef.current);
    }
  }

  return (
    <ErrorBoundary
      FallbackComponent={ErrorFallback}
      onReset={() => {
        console.error('Error boundary reset');
        reloadApp();
      }}
    >
      <AppProviders>
        <GlobalGuard>
          <RouteObserver />
          {/* WebcamStreamManager removed - use IVS Scene components directly where needed */}
          <Routes>
            <Route
              path={`${ROUTES.ADMIN}/*`}
              element={
                <AdminRoute>
                  <AdminPage />
                </AdminRoute>
              }
            />
            <Route
              path={`${ROUTES.ADMIN_TEST}`}
              element={
                <AdminRoute>
                  <AdminTestPage />
                </AdminRoute>
              }
            />
            <Route path="/test" element={<TestPage />} />
            <Route path={ROUTES.LIVEKIT_TEST} element={<LiveKitTestPage />} />
            {
              //NOTE: this is not in Admin Route as it will be used for case previewing later.
              // It's not in the Admin layout page.
            }
            <Route
              path={ROUTES.LIVE_MEETING}
              element={
                <VideoCallWrapper>
                  <LiveMeetingPermissions />
                </VideoCallWrapper>
              }
            />
            <Route path={ROUTES.PREVIEW_MEETING} element={<PreviewMeetingPermissions />} />
            <Route
              path={`${ROUTES.PREVIEW_CASE}/:accessCode`}
              element={<PreviewCasePermissions />}
            />
            <Route path={ROUTES.PREVIEW_CASE} element={<PreviewCaseCodeView />} />
            <Route path="/" element={<HomeView />} />
            <Route
              path={`${ROUTES.MEETING_STATS}/:meetingID`}
              element={
                <MeetingStatsViewContextProvider>
                  <MeetingStatsView />
                </MeetingStatsViewContextProvider>
              }
            />
            <Route
              path="*"
              element={<EmptyPageWithTitle title="Sorry..." subtitle="The page was not found." />}
            />
          </Routes>
        </GlobalGuard>
      </AppProviders>
    </ErrorBoundary>
  );
};
export default App;
